// utils/tabStateManager.js

class TabStateManager {
  constructor(storageKey, defaultTab = null, availableTabs = []) {
    this.storageKey = storageKey;
    this.defaultTab = defaultTab;
    this.availableTabs = availableTabs;
    this.listeners = [];
  }

  /**
   * Get the current active tab from localStorage
   * @returns {string} Current active tab
   */
  getActiveTab() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved && this.availableTabs.length > 0 && this.availableTabs.includes(saved)) {
        return saved;
      }
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
    }
    
    return this.defaultTab || (this.availableTabs.length > 0 ? this.availableTabs[0] : '');
  }

  /**
   * Set the active tab and save to localStorage
   * @param {string} tab - Tab to set as active
   * @returns {boolean} Success status
   */
  setActiveTab(tab) {
    if (!tab) return false;
    
    try {
      localStorage.setItem(this.storageKey, tab);
      
      // Notify listeners
      this.listeners.forEach(callback => {
        try {
          callback(tab);
        } catch (error) {
          console.warn('Tab change listener error:', error);
        }
      });
      
      return true;
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
      return false;
    }
  }

  /**
   * Reset to default tab
   * @returns {string} The reset tab
   */
  resetTab() {
    const resetTab = this.defaultTab || (this.availableTabs.length > 0 ? this.availableTabs[0] : '');
    this.setActiveTab(resetTab);
    return resetTab;
  }

  /**
   * Clear the stored tab
   */
  clearTab() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }

  /**
   * Add a listener for tab changes
   * @param {Function} callback - Function to call when tab changes
   */
  onTabChange(callback) {
    if (typeof callback === 'function') {
      this.listeners.push(callback);
    }
  }

  /**
   * Remove a tab change listener
   * @param {Function} callback - Function to remove
   */
  removeTabChangeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  /**
   * Update available tabs
   * @param {Array} tabs - New tabs array
   */
  updateAvailableTabs(tabs) {
    this.availableTabs = tabs;
    
    // Check if current saved tab is still valid
    const currentTab = this.getActiveTab();
    if (!tabs.includes(currentTab) && tabs.length > 0) {
      this.setActiveTab(this.defaultTab || tabs[0]);
    }
  }
}

/**
 * Factory function to create a tab state manager
 * @param {string} storageKey - Unique key for localStorage
 * @param {string} defaultTab - Default tab to use
 * @param {Array} availableTabs - Array of available tab names
 * @returns {TabStateManager} Tab state manager instance
 */
export function createTabStateManager(storageKey, defaultTab = null, availableTabs = []) {
  return new TabStateManager(storageKey, defaultTab, availableTabs);
}

/**
 * Simple utility functions for direct use
 */
export const TabUtils = {
  /**
   * Get tab from localStorage with fallback
   */
  getTab(storageKey, defaultTab = '', availableTabs = []) {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved && availableTabs.length > 0 && availableTabs.includes(saved)) {
        return saved;
      }
    } catch (error) {
      console.warn('Failed to read tab from localStorage:', error);
    }
    return defaultTab || (availableTabs.length > 0 ? availableTabs[0] : '');
  },

  /**
   * Save tab to localStorage
   */
  saveTab(storageKey, tab) {
    try {
      localStorage.setItem(storageKey, tab);
      return true;
    } catch (error) {
      console.warn('Failed to save tab to localStorage:', error);
      return false;
    }
  },

  /**
   * Remove tab from localStorage
   */
  clearTab(storageKey) {
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('Failed to clear tab from localStorage:', error);
    }
  }
};

// Vue Mixin for easy integration
export const TabStateMixin = {
  data() {
    return {
      tabStateManager: null,
      // Override these in your component
      tabStorageKey: 'defaultTabs',
      defaultTabValue: '',
      availableTabsList: []
    };
  },

  computed: {
    activeTab: {
      get() {
        return this.currentActiveTab;
      },
      set(value) {
        this.currentActiveTab = value;
        if (this.tabStateManager) {
          this.tabStateManager.setActiveTab(value);
        }
      }
    }
  },

  methods: {
    initTabState() {
      this.tabStateManager = createTabStateManager(
        this.tabStorageKey,
        this.defaultTabValue,
        this.availableTabsList
      );
      
      // Load initial tab
      this.currentActiveTab = this.tabStateManager.getActiveTab();
      
      // Set up listener for external changes (like other browser tabs)
      this.tabChangeListener = (e) => {
        if (e.key === this.tabStorageKey && e.newValue) {
          this.currentActiveTab = e.newValue;
        }
      };
      
      window.addEventListener('storage', this.tabChangeListener);
    },

    setActiveTab(tab) {
      this.activeTab = tab;
    },

    resetActiveTab() {
      if (this.tabStateManager) {
        this.currentActiveTab = this.tabStateManager.resetTab();
      }
    }
  },

  created() {
    this.currentActiveTab = '';
    this.initTabState();
  },

  beforeDestroy() {
    // Clean up listener
    if (this.tabChangeListener) {
      window.removeEventListener('storage', this.tabChangeListener);
    }
  }
};
